import React, { useState } from 'react';
import { Check, Loader2, Search, Trash2 } from 'lucide-react';
import type { Subject } from '@/actions/data-fetch-curriculum';
import { CARD_CATS, CATEGORY_ORDER, gradeOrder } from './curriculum-types';

// ─── Subject Matrix ───────────────────────────────────────────────────────────
export const SubjectMatrix: React.FC<{
  curriculum: any;
  savingCell: string | null;
  removingSubjectId: number | null;
  onToggle: (classId: number, subjectId: number, assigned: boolean) => Promise<void>;
  onRemoveFromMap: (subjectId: number) => Promise<void>;
}> = ({ curriculum, savingCell, removingSubjectId, onToggle, onRemoveFromMap }) => {
  const classes: any[] = [...(curriculum?.PaClasses ?? [])].sort((a: any, b: any) => gradeOrder(a.name) - gradeOrder(b.name));

  const subjectMap = new Map<number, Subject>();
  classes.forEach(cls => {
    cls.PaSubjectMaps?.forEach((m: any) => {
      if (m.subject && !subjectMap.has(m.subject.id)) subjectMap.set(m.subject.id, m.subject as Subject);
    });
  });
  const allSubjects = Array.from(subjectMap.values());

  const assigned = new Set<string>();
  classes.forEach(cls => {
    cls.PaSubjectMaps?.forEach((m: any) => assigned.add(`${cls.id}-${m.subject_id_fk}`));
  });

  const grouped = CATEGORY_ORDER.reduce<Record<string, Subject[]>>((acc, cat) => {
    const subs = allSubjects.filter(s => (s.category ?? 'core') === cat);
    if (subs.length) acc[cat] = subs;
    return acc;
  }, {});
  const uncategorised = allSubjects.filter(s => !s.category || !CATEGORY_ORDER.includes(s.category));
  if (uncategorised.length) grouped['other'] = uncategorised;

  if (classes.length === 0) return (
    <div className="empty" style={{ padding: 32 }}><p>Add classes first to use the subject map.</p></div>
  );
  if (allSubjects.length === 0) return (
    <div className="empty" style={{ padding: 32 }}><p>No subjects in map yet. Use "Add Subject" to add from the library.</p></div>
  );

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', minWidth: `${220 + classes.length * 88}px`, width: '100%' }}>
        <thead>
          <tr style={{ background: 'var(--surface)' }}>
            <th style={{ position: 'sticky', left: 0, zIndex: 20, background: 'var(--surface)', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', padding: '12px 16px', textAlign: 'left', minWidth: 240, width: 240 }}>
              <span className="meta" style={{ fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase' }}>Subject</span>
            </th>
            {classes.map(cls => {
              const count = cls.PaSubjectMaps?.length ?? 0;
              return (
                <th key={cls.id} style={{ borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', padding: '12px 8px', textAlign: 'center', minWidth: 88, width: 88 }}>
                  <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 76 }}>{cls.name}</p>
                  <p className="meta" style={{ marginTop: 2 }}>{count} {count === 1 ? 'Subject' : 'Subjects'}</p>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {Object.entries(grouped).map(([cat, subjects]) => (
            <React.Fragment key={cat}>
              <tr style={{ background: 'var(--surface-2)' }}>
                <td colSpan={classes.length + 1} style={{ position: 'sticky', left: 0, zIndex: 10, padding: '6px 16px', borderBottom: '1px solid var(--border)' }}>
                  <span className={`chip chip-sm ${CARD_CATS[CATEGORY_ORDER.indexOf(cat) % CARD_CATS.length] ?? 'cat-blue'}`} style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {cat}
                  </span>
                </td>
              </tr>
              {subjects.map((subject, idx) => (
                <tr key={subject.id}
                  style={{ background: idx % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)' }}
                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--surface-tint)'}
                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = idx % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)'}>
                  <td style={{ position: 'sticky', left: 0, zIndex: 10, borderBottom: '1px solid var(--divider)', borderRight: '1px solid var(--border)', padding: '10px 16px', minWidth: 240, width: 240, background: 'inherit' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{subject.name}</span>
                        {subject.code && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-3)', fontFamily: 'monospace' }}>{subject.code}</span>}
                      </div>
                      <button className="icon-btn"
                        style={{ width: 26, height: 26, borderRadius: 7, opacity: 0, transition: 'opacity .1s', color: 'var(--danger)' }}
                        onClick={() => onRemoveFromMap(subject.id)}
                        disabled={removingSubjectId === subject.id}
                        onMouseEnter={e => (e.currentTarget as HTMLElement).style.opacity = '1'}
                        onMouseLeave={e => (e.currentTarget as HTMLElement).style.opacity = '0'}
                        title="Remove from map">
                        {removingSubjectId === subject.id
                          ? <Loader2 size={13} style={{ animation: 'pa-spin .7s linear infinite' }} />
                          : <Trash2 size={13} />}
                      </button>
                    </div>
                  </td>
                  {classes.map(cls => {
                    const key = `${cls.id}-${subject.id}`;
                    const isAssigned = assigned.has(key);
                    const isSaving = savingCell === key;
                    return (
                      <td key={cls.id} style={{ borderBottom: '1px solid var(--divider)', borderRight: '1px solid var(--border)', textAlign: 'center', padding: '10px 8px' }}>
                        <button
                          onClick={() => onToggle(cls.id, subject.id, isAssigned)}
                          disabled={isSaving}
                          title={isAssigned ? 'Remove from this class' : 'Assign to this class'}
                          style={{
                            width: 28, height: 28, borderRadius: 8,
                            border: `2px solid ${isSaving ? 'var(--border-2)' : isAssigned ? 'var(--slate)' : 'var(--border-2)'}`,
                            background: isAssigned ? 'var(--slate)' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', cursor: isSaving ? 'wait' : 'pointer', transition: 'all .12s',
                          }}
                          onMouseEnter={e => { if (!isSaving && isAssigned) { (e.currentTarget as HTMLElement).style.background = 'var(--danger)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--danger)'; } else if (!isSaving) { (e.currentTarget as HTMLElement).style.borderColor = 'var(--slate)'; } }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isAssigned ? 'var(--slate)' : 'transparent'; (e.currentTarget as HTMLElement).style.borderColor = isAssigned ? 'var(--slate)' : 'var(--border-2)'; }}>
                          {isSaving
                            ? <Loader2 size={12} style={{ animation: 'pa-spin .7s linear infinite', color: 'var(--text-3)' }} />
                            : isAssigned ? <Check size={13} strokeWidth={2.5} style={{ color: '#fff' }} /> : null}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Add to Map Panel ─────────────────────────────────────────────────────────
export const AddToMapPanel: React.FC<{
  subjectMaster: Subject[];
  curriculum: any;
  onAdd: (subjectIds: number[], classIds: number[]) => Promise<void>;
  onClose: () => void;
}> = ({ subjectMaster, curriculum, onAdd, onClose }) => {
  const classes: any[] = [...(curriculum?.PaClasses ?? [])].sort((a: any, b: any) => gradeOrder(a.name) - gradeOrder(b.name));

  const inMapIds = new Set<number>();
  classes.forEach(cls => cls.PaSubjectMaps?.forEach((m: any) => inMapIds.add(m.subject_id_fk)));
  const available = subjectMaster.filter(s => !inMapIds.has(s.id));

  const [search, setSearch] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState<number[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const filtered = available.filter(s => s.name.toLowerCase().includes(search.toLowerCase()));
  const toggleSubject = (id: number) => setSelectedSubjects(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleClass = (id: number) => setSelectedClasses(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleAdd = async () => {
    if (!selectedSubjects.length || !selectedClasses.length || saving) return;
    setSaving(true);
    try { await onAdd(selectedSubjects, selectedClasses); onClose(); } finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <p className="meta" style={{ fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10 }}>Select Subjects</p>
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search library…"
            className="input" style={{ paddingLeft: 36 }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 260, overflowY: 'auto' }}>
          {filtered.length === 0
            ? <p className="meta" style={{ padding: '12px 0', textAlign: 'center' }}>{search ? 'No match' : 'All library subjects are already in the map'}</p>
            : filtered.map(s => {
              const checked = selectedSubjects.includes(s.id);
              return (
                <button key={s.id} onClick={() => toggleSubject(s.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left', background: checked ? 'var(--accent-wash)' : 'transparent', transition: 'background .1s' }}
                  onMouseEnter={e => { if (!checked) (e.currentTarget as HTMLElement).style.background = 'var(--surface-tint)'; }}
                  onMouseLeave={e => { if (!checked) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${checked ? 'var(--accent-deep)' : 'var(--border-2)'}`, background: checked ? 'var(--accent-deep)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {checked && <Check size={10} style={{ color: '#fff' }} />}
                  </div>
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 500, color: 'var(--text)' }}>{s.name}</span>
                  {s.code && <span className="meta" style={{ fontFamily: 'monospace' }}>{s.code}</span>}
                </button>
              );
            })
          }
        </div>
      </div>

      {selectedSubjects.length > 0 && (
        <div style={{ borderTop: '1px solid var(--divider)', paddingTop: 20 }}>
          <p className="meta" style={{ fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10 }}>Assign to Classes</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 200, overflowY: 'auto' }}>
            {classes.map(cls => {
              const checked = selectedClasses.includes(cls.id);
              return (
                <button key={cls.id} onClick={() => toggleClass(cls.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left', background: checked ? 'var(--accent-wash)' : 'transparent', transition: 'background .1s' }}
                  onMouseEnter={e => { if (!checked) (e.currentTarget as HTMLElement).style.background = 'var(--surface-tint)'; }}
                  onMouseLeave={e => { if (!checked) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${checked ? 'var(--accent-deep)' : 'var(--border-2)'}`, background: checked ? 'var(--accent-deep)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {checked && <Check size={10} style={{ color: '#fff' }} />}
                  </div>
                  <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text)' }}>{cls.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selectedSubjects.length > 0 && selectedClasses.length > 0 && (
        <button onClick={handleAdd} disabled={saving} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
          {saving
            ? <><Loader2 size={14} style={{ animation: 'pa-spin .7s linear infinite' }} /> Adding…</>
            : <>Add {selectedSubjects.length} subject{selectedSubjects.length > 1 ? 's' : ''} to {selectedClasses.length} class{selectedClasses.length > 1 ? 'es' : ''}</>
          }
        </button>
      )}
    </div>
  );
};
